import {
  Box,
  Button,
  CollectionPreferencesProps,
  ExpandableSection,
  Header,
  Input,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  SpaceBetween,
  TableProps,
} from '@amzn/awsui-components-core';
import {
  ApproverType,
  ContentWorkflow,
  PublisherReviewStatus,
  WorkflowTag,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, useCallback } from 'react';
import {
  deepClone,
  getDefaultFilterSelection,
  getFilterCounterText,
  getHeaderCounterText,
} from '../../api/UtilsApi';
import constants from '../../common/constants';
import LocalStorage from '../../common/LocalStorage';
import { ContentAttributes, WorkflowActions } from '../../model/Enum';
import FilterSelectionAttributes from '../../model/FilterSelectionAttributes';
import TableSettingOptions from '../../model/TableSettingOptions';
import WorkflowActionControls from '../administration/WorkflowActionControls';
import { TableNoMatchState } from '../common/CommonComponents';
import {
  BR_APPROVED_FILTER_VALUE,
  BR_PENDING_APPROVAL_FILTER_VALUE,
  BrWorkflowStateOptions,
  ContentTypeOptions,
  DashboardContentTypeOptions,
  DashboardWorkflowStateOptions,
  WorkflowStateOptions,
  workflowTagOptionsMap,
} from '../utils/SelectableOptions';
import { getContentWorkflowTags } from '../utils/WorkflowTagUtils';
import {
  ContentWorkflowCsvFormat,
  DefaultContentSelectorOptions,
  DefaultSortableColumns,
} from './ContentWorkflowColumnDefinitions';
import PaginatedTable, { CustomPreferences } from './PaginatedTable';
import AppContextProps from '../../contexts/AppContextProps';
import { withAppContext } from '../../contexts/AppContext';
import { convertEntities } from '@amzn/awscat-editor';
import { DefaultTablePreferences } from './ContentWorkflowTableDefinitions';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';

enum FilterType {
  contentType = 'contentType',
  state = 'state',
  workflowTag = 'workflowTag',
}
interface Props extends AppContextProps {
  tableId: string;
  tableType?: 'simple' | 'full';
  header?: string;
  workflows: ContentWorkflow[];
  columnDefinitions: Array<TableProps.ColumnDefinition<ContentWorkflow>>;
  onSelectionChange?: (selectedPatterns: ContentWorkflow[]) => void;
  onRefreshClick: (condition?: (workflows: ContentWorkflow[]) => boolean) => void;
  loading?: boolean;
  selectionType?: TableProps.SelectionType;
  allowedControlActions?: WorkflowActions[];
  allowedFilterTypes?: FilterType[];
  defaultVisibleColumnIds?: string[];
}
const csvFormat: Array<{ header: string; value: (i: ContentWorkflow) => any }> =
  ContentWorkflowCsvFormat;
const defaultContentType = getDefaultFilterSelection(constants.TABLE_DEFAULT_CONTENT_TYPE);
const defaultWorkflowState = getDefaultFilterSelection(constants.TABLE_DEFAULT_WORKFLOW_STATE);
const defaultWorkflowTag = getDefaultFilterSelection(constants.TABLE_DEFAULT_WORKFLOW_TAG);

const ContentWorkflowTable: React.FunctionComponent<Props> = (props: Props) => {
  const isEarlySharingEnabled = useFeatureFlag('EarlySharing');
  const filterContentTypeOptions = [
    defaultContentType,
    ...(props.tableType === 'simple' ? ContentTypeOptions : DashboardContentTypeOptions),
  ];
  const dashboardStateOptions = DashboardWorkflowStateOptions.filter(
    (opt) => opt.value !== WorkflowState.PROPOSAL,
  );
  const filterWorkflowStateOptions = [
    defaultWorkflowState,
    ...(props.tableType === 'simple' ? WorkflowStateOptions : dashboardStateOptions),
    ...(isEarlySharingEnabled ? BrWorkflowStateOptions : []),
  ];
  const filterWorkflowTagOptions = [
    defaultWorkflowTag,
    ...workflowTagOptionsMap.filter((opt) => opt.value !== WorkflowTag.TEST),
  ];

  const columnDefinitionsWithSorting = props.columnDefinitions.map((column) => {
    const sortingCol = DefaultSortableColumns.find((sortCol) => sortCol.id === column.id);
    if (sortingCol) {
      column.sortingComparator = sortingCol.comparator;
    }
    return column;
  });

  const contentSelectorOptions: CollectionPreferencesProps.ContentDisplayOption[] =
    DefaultContentSelectorOptions.filter((o) =>
      props.columnDefinitions.some((c) => c.id === o.id),
    ).map((options) => {
      const contentDisplayOption: CollectionPreferencesProps.ContentDisplayOption = {
        id: options.id,
        label: options.label,
        alwaysVisible: !options.editable,
      };
      return contentDisplayOption;
    });
  const contentDiscriptionGroup: CollectionPreferencesProps.ContentDisplayPreference = {
    description: 'Attributes',
    options: contentSelectorOptions,
  };

  const getContentType = (filterSelectionAttributes: FilterSelectionAttributes) => {
    if (!filterSelectionAttributes) {
      return defaultContentType;
    }
    const { contentType: cachedContentType } = filterSelectionAttributes;
    return (
      filterContentTypeOptions.find((contentType) => contentType.value === cachedContentType) ||
      defaultContentType
    );
  };

  const getWorkflowState = (filterSelectionAttributes: FilterSelectionAttributes) => {
    if (!filterSelectionAttributes) {
      return defaultWorkflowState;
    }
    const { workflowState: cachedWorkflowState } = filterSelectionAttributes;
    return (
      filterWorkflowStateOptions.find(
        (workflowState) => workflowState.value === cachedWorkflowState,
      ) || defaultWorkflowState
    );
  };

  const getWorkflowTag = (filterSelectionAttributes: FilterSelectionAttributes) => {
    if (!filterSelectionAttributes) {
      return defaultWorkflowTag;
    }
    const { workflowTag: cachedWorkflowTag } = filterSelectionAttributes;
    return (
      filterWorkflowTagOptions.find((workflowTag) => workflowTag.value === cachedWorkflowTag) ||
      defaultWorkflowTag
    );
  };

  const initializeSelectedContentType = (): SelectProps.Option => {
    const existingTableSettingOptions = LocalStorage.getTableSettingOptions(props.tableId);
    return getContentType(existingTableSettingOptions?.filterSelectionAttributes);
  };

  const initializeSelectedWorkflowState = (): SelectProps.Option => {
    const existingTableSettingOptions = LocalStorage.getTableSettingOptions(props.tableId);
    return getWorkflowState(existingTableSettingOptions?.filterSelectionAttributes);
  };

  const initializeSelectedWorkflowTag = (): SelectProps.Option => {
    const existingTableSettingOptions = LocalStorage.getTableSettingOptions(props.tableId);
    return getWorkflowTag(existingTableSettingOptions?.filterSelectionAttributes);
  };

  const initializeTableSettingOptionsCache = (): TableSettingOptions => {
    const existingTablePreferenceCache = LocalStorage.getTableSettingOptions(props.tableId);
    if (existingTablePreferenceCache?.visibleColumns?.size > 0) {
      return existingTablePreferenceCache;
    } else {
      return Object.assign(existingTablePreferenceCache, {
        ...DefaultTablePreferences,
        filterSelectionAttributes: {
          contentType: defaultContentType.label,
          workflowState: defaultWorkflowState.label,
        },
        wrapLines: true,
      });
    }
  };

  const [tableSettingOptionsCache, setTableSettingOptionsCache] = useState<TableSettingOptions>(
    initializeTableSettingOptionsCache(),
  );
  const [tableSettingOptions, setTableSettingOptions] = useState<TableSettingOptions>(
    deepClone(tableSettingOptionsCache),
  );
  const [workflows, setWorkflows] = useState<ContentWorkflow[]>(props.workflows);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<Set<string>>(new Set());
  const [selectedContentType, setSelectedContentType] = useState<SelectProps.Option>(
    initializeSelectedContentType(),
  );
  const [selectedWorkflowState, setSelectedWorkflowState] = useState<SelectProps.Option>(
    initializeSelectedWorkflowState(),
  );
  const [selectedWorkflowTag, setSelectedWorkflowTag] = useState<SelectProps.Option>(
    initializeSelectedWorkflowTag(),
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [filteringText, setFilteringText] = useState<string>('');

  const [columnContentDisplay, setColumnContentDisplay] = useState<
    CollectionPreferencesProps.ContentDisplayItem[]
  >(
    DefaultContentSelectorOptions.filter((o) =>
      props.columnDefinitions.some((c) => c.id === o.id),
    ).map((content) => {
      const contentDisplayOption: CollectionPreferencesProps.ContentDisplayItem = {
        id: content.id,
        // @ts-expect-error TS2322 - strict migration
        visible:
          content.visible ||
          tableSettingOptions?.visibleColumns?.has(content.id) ||
          props.defaultVisibleColumnIds?.includes(content.id),
      };
      return contentDisplayOption;
    }),
  );

  const updateTableWorkflows = useCallback(() => {
    const entities = props.appContext?.entities || {};
    const contentTypeFilter = (workflow: ContentWorkflow) =>
      selectedContentType.value === defaultContentType.id ||
      workflow.draftContentMetadata.contentType === selectedContentType.value;

    const workflowStateFilter = (workflow: ContentWorkflow) => {
      if (selectedWorkflowState.value === defaultWorkflowState.id) return true;

      if (selectedWorkflowState.value === BR_APPROVED_FILTER_VALUE) {
        return (
          workflow.publisherReviewInfo?.reviewStatus === PublisherReviewStatus.APPROVED &&
          workflow.publisherReviewInfo?.approverType === ApproverType.BAR_RAISER
        );
      }

      if (selectedWorkflowState.value === BR_PENDING_APPROVAL_FILTER_VALUE) {
        return workflow.publisherReviewInfo?.reviewStatus === PublisherReviewStatus.PENDING;
      }

      const matchingStates =
        selectedWorkflowState.value === WorkflowState.PUBLISHED
          ? [WorkflowState.PUBLISHED, WorkflowState.FINALIZED]
          : [selectedWorkflowState.value];
      return (
        matchingStates.includes(workflow.draftState as WorkflowState) ||
        workflow.publicationWorkflows.some((wf) =>
          matchingStates.includes(wf.state as WorkflowState),
        )
      );
    };

    const workflowTagFilter = (workflow: ContentWorkflow) =>
      selectedWorkflowTag.value === defaultWorkflowTag.id ||
      // @ts-expect-error TS2345 - strict migration
      getContentWorkflowTags(workflow).has(selectedWorkflowTag.value);

    const filteringTextLowerCase = filteringText.toLowerCase().trim();

    // @ts-expect-error TS7006 - strict migration
    const inputFilterFn = (object) =>
      Object.keys(object).some(
        (k) =>
          typeof object[k] === 'string' &&
          convertEntities(object[k], entities).toLowerCase().indexOf(filteringTextLowerCase) > -1,
      );

    const newWorkflows = props.workflows.filter(
      (workflow) =>
        contentTypeFilter(workflow) &&
        workflowStateFilter(workflow) &&
        workflowTagFilter(workflow) &&
        (filteringTextLowerCase.length < constants.FILTER_INPUT_MIN_LENGTH ||
          inputFilterFn(workflow) ||
          inputFilterFn(workflow.draftContentMetadata)),
    );
    setWorkflows(newWorkflows);
  }, [
    selectedContentType,
    selectedWorkflowState,
    selectedWorkflowTag,
    filteringText,
    props.workflows,
    props.appContext,
  ]);

  useEffect(() => {
    updateTableWorkflows();
  }, [updateTableWorkflows]);

  const changeTableSetting = (
    e: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences<CustomPreferences>>,
  ) => {
    const { contentDisplay, pageSize: size, wrapLines: wrapLinesFlag, custom } = e.detail;
    const updatedTablePreferencesCache = tableSettingOptionsCache;
    if (contentDisplay !== undefined) {
      updatedTablePreferencesCache.visibleColumns = new Set(
        contentDisplay?.filter((content) => !!content.visible).map((content) => content.id),
      );
      setColumnContentDisplay([...(contentDisplay ?? [])]);
    }
    if (size !== undefined) {
      updatedTablePreferencesCache.pageSize = size;
    }
    if (wrapLinesFlag !== undefined) {
      updatedTablePreferencesCache.wrapLines = wrapLinesFlag;
    }
    if (custom?.stickyHeader !== undefined) {
      updatedTablePreferencesCache.stickyHeader = custom?.stickyHeader;
    }
    if (custom?.clearFilterOnRefresh !== undefined) {
      updatedTablePreferencesCache.clearFiltersOnRefresh = custom?.clearFilterOnRefresh;
    }
    LocalStorage.setTableSettingOptions(props.tableId, updatedTablePreferencesCache);
    setTableSettingOptionsCache(updatedTablePreferencesCache);
    setTableSettingOptions(deepClone(updatedTablePreferencesCache));
  };

  const inputFilterKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      updateTableWorkflows();
    }
  };

  const selectContentTypeFilter = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    const { selectedOption } = event.detail;
    if (selectedOption.value) {
      tableSettingOptions.filterSelectionAttributes =
        tableSettingOptions.filterSelectionAttributes || new FilterSelectionAttributes();
      tableSettingOptions.filterSelectionAttributes.contentType = selectedOption.value;
    }
    LocalStorage.setTableSettingOptions(props.tableId, tableSettingOptions);
    setSelectedContentType(selectedOption);
    setTableSettingOptions(tableSettingOptions);
  };

  const selectWorkflowStateFilter = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    const { selectedOption } = event.detail;
    if (selectedOption.value) {
      tableSettingOptions.filterSelectionAttributes =
        tableSettingOptions.filterSelectionAttributes || new FilterSelectionAttributes();
      tableSettingOptions.filterSelectionAttributes.workflowState = selectedOption.value;
    }
    LocalStorage.setTableSettingOptions(props.tableId, tableSettingOptions);
    setSelectedWorkflowState(selectedOption);
    setTableSettingOptions(tableSettingOptions);
  };

  const selectWorkflowTagFilter = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    const { selectedOption } = event.detail;
    if (selectedOption.value) {
      tableSettingOptions.filterSelectionAttributes =
        tableSettingOptions.filterSelectionAttributes || new FilterSelectionAttributes();
      tableSettingOptions.filterSelectionAttributes.workflowTag = selectedOption.value;
    }
    LocalStorage.setTableSettingOptions(props.tableId, tableSettingOptions);
    setSelectedWorkflowTag(selectedOption);
    setTableSettingOptions(tableSettingOptions);
  };

  const clearFilter = () => {
    tableSettingOptions.filterSelectionAttributes = Object.assign(new FilterSelectionAttributes(), {
      contentType: constants.TABLE_DEFAULT_CONTENT_TYPE,
      workflowState: constants.TABLE_DEFAULT_WORKFLOW_STATE,
      workflowTag: constants.TABLE_DEFAULT_WORKFLOW_TAG,
    });
    LocalStorage.setTableSettingOptions(props.tableId, tableSettingOptions);

    setFilteringText('');
    setSelectedContentType(defaultContentType);
    setSelectedWorkflowState(defaultWorkflowState);
    setSelectedWorkflowTag(defaultWorkflowTag);
    setTableSettingOptions(tableSettingOptions);
  };

  const changeSelection = (
    event: NonCancelableCustomEvent<TableProps.SelectionChangeDetail<ContentWorkflow>>,
  ) => {
    const selectedIds = event.detail.selectedItems.map((item) => item.contentId);
    setSelectedWorkflowIds(new Set(selectedIds));
  };

  const updatedItem = async (updatedAttribute?: string, updatedValue?: any) => {
    let checkingCondition;

    switch (updatedAttribute) {
      case ContentAttributes.Author:
        checkingCondition = (wfs: ContentWorkflow[]) =>
          wfs.some(
            (i) => selectedWorkflowIds.has(i.contentId) && i.ownerId !== updatedValue.authorId,
          );
        break;
      default:
        break;
    }

    return handleRefresh(null, checkingCondition);
  };

  const deletedItem = async (deletedContentWorkflows: ContentWorkflow[]) => {
    const checkingCondition = (wfs: ContentWorkflow[]) =>
      wfs.some((i) => deletedContentWorkflows.some((swf) => swf.contentId === i.contentId));
    setSelectedWorkflowIds(new Set());
    return handleRefresh(null, checkingCondition);
  };

  const postApgAction = async (wf: ContentWorkflow[]) => {
    const checkingCondition = (wfs: ContentWorkflow[]) =>
      wfs.some(
        (i) =>
          !!i.apgPublicationDate &&
          wf.some(
            (w) =>
              w.contentId === i.contentId &&
              w.apgPublicationDate &&
              // @ts-expect-error TS2345 - strict migration
              w.apgPublicationDate.equals(i.apgPublicationDate),
          ),
      );
    return handleRefresh(null, checkingCondition);
  };

  const handleRefresh = (event: any, condition?: any) => {
    if (!!event && tableSettingOptions.clearFiltersOnRefresh) {
      clearFilter();
    }
    props.onRefreshClick(condition);
  };

  const selectedWorkflows = workflows.filter((wf) => selectedWorkflowIds.has(wf.contentId));
  const actionControls =
    Array.isArray(props.allowedControlActions) && props.allowedControlActions.length ? (
      <WorkflowActionControls
        // Remount when the selection or a selected item's audience, version or state changes:
        // WorkflowActionControls derives some actions (e.g. the demote list) in its constructor, and a
        // refresh after an action keeps the selection. A Set iterator stringifies to a constant.
        key={selectedWorkflows
          .map((wf) =>
            [
              wf.contentId,
              wf.contentAudience,
              wf.draftState,
              wf.draftWorkflowId,
              ...wf.publicationWorkflows.map(
                (p) => `${p.contentAudience}|${p.state}|${p.workflowId}`,
              ),
            ].join(':'),
          )
          .join(',')}
        workflows={selectedWorkflows}
        onWorkflowUpdated={updatedItem}
        onWorkflowDeleted={deletedItem}
        onWorkflowPermissionUpdate={handleRefresh}
        onWorkflowReviewersUpdate={handleRefresh}
        onWorkflowUnpublished={handleRefresh}
        onAPGPublished={postApgAction}
        onAPGUnpublished={postApgAction}
        allowedActions={props.allowedControlActions}
      />
    ) : null;

  const headerComp = (
    <Header
      variant="h3"
      counter={`${getHeaderCounterText(props.workflows, Array.from(selectedWorkflowIds.values()))}`}
      actions={
        <SpaceBetween size="s" direction="horizontal">
          {actionControls}
          <Box padding={{ top: 'xs' }} float="right">
            <Button
              variant="icon"
              iconName="refresh"
              ariaLabel="Refresh table"
              onClick={handleRefresh}
            />
          </Box>
        </SpaceBetween>
      }
    >
      {`${props.header || 'Total'} `}
    </Header>
  );

  const tableEmptyState =
    Array.isArray(props.workflows) && props.workflows.length ? (
      <TableNoMatchState onClearFilter={clearFilter} />
    ) : (
      'No content'
    );

  const filterByType = props.allowedFilterTypes?.includes(FilterType.contentType) ? (
    <Select
      data-testid="content-type-filter"
      options={filterContentTypeOptions}
      selectedOption={selectedContentType}
      onChange={selectContentTypeFilter}
      ariaRequired
    />
  ) : null;

  const filterByState = props.allowedFilterTypes?.includes(FilterType.state) ? (
    <Select
      data-testid="workflow-state-filter"
      options={filterWorkflowStateOptions}
      selectedOption={selectedWorkflowState}
      onChange={selectWorkflowStateFilter}
      ariaRequired
    />
  ) : null;

  const filterByTag = props.allowedFilterTypes?.includes(FilterType.workflowTag) ? (
    <Select
      data-testid="workflow-tag-filter"
      options={filterWorkflowTagOptions}
      selectedOption={selectedWorkflowTag}
      onChange={selectWorkflowTagFilter}
      ariaRequired
    />
  ) : null;

  const hasActiveDropdownFilters =
    selectedContentType.value !== defaultContentType.value ||
    selectedWorkflowState.value !== defaultWorkflowState.value ||
    selectedWorkflowTag.value !== defaultWorkflowTag.value;

  const filterByText =
    (filteringText || hasActiveDropdownFilters) && getFilterCounterText(workflows.length);

  const activeFilterCount = [
    selectedContentType.value !== defaultContentType.value,
    selectedWorkflowState.value !== defaultWorkflowState.value,
    selectedWorkflowTag.value !== defaultWorkflowTag.value,
  ].filter(Boolean).length;

  const dropdownFilters = [filterByType, filterByState, filterByTag].filter(Boolean);
  const useExpandableFilters = dropdownFilters.length > constants.TABLE_MAX_INLINE_FILTERS;

  const dropdownFiltersContent =
    dropdownFilters.length > 0 ? (
      <SpaceBetween size="s" direction="horizontal">
        {filterByType}
        {filterByState}
        {filterByTag}
      </SpaceBetween>
    ) : null;

  const filters = (
    <SpaceBetween size="xs" direction="vertical">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: '1 1 auto', minWidth: '300px', maxWidth: '800px' }}>
          <Input
            data-testid="input-filter"
            value={filteringText}
            type="search"
            onChange={({ detail }) => setFilteringText(detail.value)}
            onKeyDown={inputFilterKeyDown}
            placeholder={constants.FIND_CONTENT_PLACEHOLDER}
            ariaRequired
          />
        </div>
        {!useExpandableFilters && dropdownFiltersContent}
        <Box padding={{ top: 'xs' }}>{filterByText}</Box>
      </div>
      {useExpandableFilters && (
        <ExpandableSection
          headerText={`Filters${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
          variant="footer"
        >
          {dropdownFiltersContent}
        </ExpandableSection>
      )}
    </SpaceBetween>
  );

  return (
    <PaginatedTable
      data-testid="paginated-table"
      loading={props.loading || isUpdating}
      header={headerComp}
      empty={tableEmptyState}
      columnDefinitions={columnDefinitionsWithSorting}
      items={workflows}
      csvFormat={csvFormat}
      resizableColumns
      tableContentSelectorOptions={contentDiscriptionGroup}
      onTableSettingChange={changeTableSetting}
      onSelectionChange={changeSelection}
      selectionType={props.selectionType}
      filter={filters}
      columnDisplay={columnContentDisplay}
      selectedItems={workflows.filter((wf) => selectedWorkflowIds.has(wf.contentId))}
      tablePreferences={tableSettingOptions}
    />
  );
};

export default withAppContext(ContentWorkflowTable);
